import type { FC } from 'react'
import { Controller } from 'react-hook-form'
import { CharCounter } from '@/components/commons/char-counter'
import { InputText } from '@/components/commons/input-text'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowRightIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { useChangeStep } from '@/features/experience-shell/hooks/use-change-step'
import { EXPERIENCE_OUTPUT_CHARACTER_LIMITS } from '@/features/experience-vetting/form-schemas'
import { useTranslation } from '@/translations/i18n'
import type { ShellPartialFormSchema } from '../form-schemas'
import { useExperienceShellFormContext } from '../hooks/use-experience-shell-form-context'
import { StepKeyName } from '../types'
import { ColumnFields, Content, Fieldset, Footer } from './atoms'
import { CountriesSelect } from './countries-select'

export const LocationStep: FC<{ experienceId: number; isUpgrade: boolean }> = ({
	experienceId,
	isUpgrade
}) => {
	const { t } = useTranslation()
	const changeStep = useChangeStep({
		experienceId,
		currentStep: StepKeyName.enum.location,
		isUpgrade
	})
	const {
		control,
		register,
		formState: { errors }
	} = useExperienceShellFormContext(isUpgrade)

	return (
		<>
			<Content title={t('experience_shell:step_location')}>
				<div className='flex flex-col gap-6'>
					<Fieldset title={t('experience_shell:meeting_point')}>
						<Controller
							control={control}
							name='location.country_region'
							render={({ field }) => (
								<CountriesSelect
									errorMessage={
										errors.location?.country_region
											? t('errors:field_required')
											: undefined
									}
									onChange={field.onChange}
									value={field.value ?? ''}
								/>
							)}
						/>
						<InputText
							errorMessage={
								errors.location?.location_address
									? t('errors:field_required')
									: undefined
							}
							inputSize='sm'
							label={t('experience_shell:street_address')}
							type='text'
							{...register('location.location_address')}
						/>
						<InputText
							inputSize='sm'
							label={t('experience_shell:apt_suite')}
							type='text'
							{...register('location.location_address_extras')}
						/>
						<InputText
							errorMessage={
								errors.location?.location_city
									? t('errors:field_required')
									: undefined
							}
							inputSize='sm'
							label={t('experience_shell:city')}
							type='text'
							{...register('location.location_city')}
						/>
						<ColumnFields>
							<InputText
								errorMessage={
									errors.location?.location_state
										? t('errors:field_required')
										: undefined
								}
								inputSize='sm'
								label={t('experience_shell:state_territory')}
								type='text'
								{...register('location.location_state')}
							/>
							<InputText
								errorMessage={
									errors.location?.location_zip
										? t('errors:field_required')
										: undefined
								}
								inputSize='sm'
								label={t('experience_shell:zip_code')}
								type='text'
								{...register('location.location_zip')}
							/>
						</ColumnFields>
					</Fieldset>
					<Separator orientation='horizontal' />
					<Fieldset
						title={t('experience_shell:meeting_point_details')}
					>
						<div className='flex w-full flex-col'>
							<InputText
								inputSize='sm'
								label={t('experience_shell:name')}
								placeholder={t('experience_shell:enter_name')}
								type='text'
								{...register('location.meeting_point_name')}
								errorMessage={
									errors.location?.meeting_point_name
										? t('errors:field_required')
										: undefined
								}
							/>
							<CharCounter<ShellPartialFormSchema>
								isDisabled={false}
								maxLength={
									EXPERIENCE_OUTPUT_CHARACTER_LIMITS
										.EXPERIENCE.LOCATION_NAME
								}
								name='location.meeting_point_name'
							/>
						</div>
					</Fieldset>
				</div>
			</Content>
			<Footer>
				<ButtonLegacy
					disabled={
						changeStep.isBackLoading || changeStep.isContinueLoading
					}
					onClick={() =>
						changeStep.handleChangeStep({
							isBack: true,
							stepToGoTo: StepKeyName.enum.merchandising
						})
					}
					type='button'
					variant='tertiary'
				>
					{t('common:back')}
					{changeStep.isBackLoading && <Spinner />}
				</ButtonLegacy>
				<ButtonLegacy
					disabled={
						changeStep.isContinueLoading || changeStep.isBackLoading
					}
					onClick={() =>
						changeStep.handleChangeStep({
							stepToGoTo: StepKeyName.enum.photos
						})
					}
					type='button'
				>
					{t('common:continue')}
					{changeStep.isContinueLoading ? (
						<Spinner />
					) : (
						<ArrowRightIcon />
					)}
				</ButtonLegacy>
			</Footer>
		</>
	)
}
